import SettingsContent from "./components/SettingsContent";
import DataManagementView from "./components/DataManagementView";
import SettingLoadingSkeleton from "./components/SettingLoadingSkeleton";
import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import { Settings, Database, FileX } from "lucide-react";
import { SettingsProvider, useSettingsContext } from "./context/SettingsContext";
import { SettingsData } from "./constants/settingsStructure";

const SettingsViewContent = () => {
  const { selectedCategory, setSelectedCategory, isDataManagementOpen, setIsDataManagementOpen, regularCategories, aiPromptCategories, isFetching, csvData, isPreviewMode, user } =
    useSettingsContext();

  // Show loading state with skeleton
  if (isFetching) {
    return <SettingLoadingSkeleton />;
  }

  // Show empty state if no CSV data and no categories
  const hasNoData = !csvData || Object.keys(csvData).length === 0;
  const hasNoCategories = regularCategories.length === 0 && aiPromptCategories.length === 0;

  if (hasNoData && hasNoCategories) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-6">
          <FileX className="h-12 w-12 text-muted-foreground" />
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">No Settings Found</h3>
            <p className="text-sm text-muted-foreground">No settings data found. {user.is_superuser ? "You can import settings from a CSV file or use the default settings structure." : "Please contact your administrator to configure settings."}</p>
          </div>
          {user.is_superuser && (
            <Button onClick={() => setIsDataManagementOpen(true)} variant="outline" className="mt-4">
              <Database className="h-4 w-4 mr-2" />
              Open Data Management
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      {/* Left Panel - Categories Sidebar */}
      <div className="w-64 border-r border-border flex flex-col bg-background">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 px-5 h-[3.75rem] flex items-center justify-between border-b border-border bg-background">
          <h2 className="text-lg font-semibold text-foreground">{isPreviewMode ? "Settings Preview" : "Settings"}</h2>
          {user.is_superuser && !isPreviewMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" title="Open Legacy Settings Editor">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setIsDataManagementOpen(true)}>
                  <Database className="h-4 w-4" />
                  <span>Data Management</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {/* Scrollable Categories List */}
        <ul className="flex-1 overflow-y-auto p-3 space-y-1">
          {/* Regular Categories */}
          {regularCategories.map((category) => (
            <li
              key={category.id}
              onClick={() => setSelectedCategory(category)}
              className={cn(
                "px-3 py-2 cursor-pointer hover:bg-primary/5 flex items-center gap-3 transition-all duration-150 rounded-lg",
                selectedCategory?.id === category.id && "bg-primary/10 border-l-4 border-primary"
              )}
            >
              <i className={cn(
                category.icon,
                "text-base",
                selectedCategory?.id === category.id ? "text-primary" : "text-muted-foreground"
              )} />
              <span className={cn(
                "text-sm font-medium",
                selectedCategory?.id === category.id ? "text-foreground" : "text-foreground/90"
              )}>
                {category.label}
              </span>
            </li>
          ))}

          {/* AI Prompt Categories */}
          {aiPromptCategories.map((category) => (
            <li
              key={category.id}
              onClick={() => setSelectedCategory(category)}
              className={cn(
                "px-3 py-2 cursor-pointer hover:bg-primary/5 flex items-center gap-3 transition-all duration-150 rounded-lg",
                selectedCategory?.id === category.id && "bg-primary/10 border-l-4 border-primary"
              )}
            >
              <i className={cn(
                category.icon,
                "text-base",
                selectedCategory?.id === category.id ? "text-primary" : "text-muted-foreground"
              )} />
              <span className={cn(
                "text-sm font-medium",
                selectedCategory?.id === category.id ? "text-foreground" : "text-foreground/90"
              )}>
                {category.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        <SettingsContent />
      </div>

      {/* Data Management Dialog - Only show if not in preview mode */}
      {!isPreviewMode && (
        <Dialog open={isDataManagementOpen} onOpenChange={setIsDataManagementOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Data Management</DialogTitle>
              <DialogDescription>Export and import settings data across application.</DialogDescription>
            </DialogHeader>
            <DataManagementView />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

interface SettingsViewProps {
  previewData?: SettingsData | null;
  isPreviewMode?: boolean;
}

const SettingsView = ({ previewData, isPreviewMode = false }: SettingsViewProps) => {
  return (
    <SettingsProvider previewData={previewData} isPreviewMode={isPreviewMode}>
      <SettingsViewContent />
    </SettingsProvider>
  );
};

export default SettingsView;
